import React from 'react';
import { ShieldCheck, Zap, Activity, Lock, Globe, Clock, Database, Cloud } from 'lucide-react';
import { motion } from 'framer-motion';

const getIcon = (text) => {
  if (text.includes('Latency')) return <Zap size={16} />;
  if (text.includes('Uptime')) return <Activity size={16} />;
  if (text.includes('Security') || text.includes('Lock')) return <Lock size={16} />;
  if (text.includes('Carrier')) return <Globe size={16} />;
  if (text.includes('RPO') || text.includes('RTO')) return <Clock size={16} />;
  if (text.includes('Backup')) return <Database size={16} />;
  if (text.includes('Separation')) return <Cloud size={16} />;
  return <ShieldCheck size={16} />;
};

const ZoneCard = ({ zone, isActive, isOffline, isDarkMode }) => {
  const getColorClasses = (color) => {
    // DARK MODE STYLES
    if (isDarkMode) {
        switch (color) {
        case 'emerald':
            return isOffline ? 'border-red-900/50 bg-red-900/10' : 'border-emerald-500/50 bg-emerald-900/10 shadow-[0_0_30px_rgba(16,185,129,0.15)]';
        case 'amber':
            return isOffline ? 'border-red-900/50 bg-red-900/10' : 'border-amber-500/50 bg-amber-900/10 shadow-[0_0_30px_rgba(245,158,11,0.15)]';
        case 'blue':
            return isOffline ? 'border-red-900/50 bg-red-900/10' : 'border-blue-500/50 bg-blue-900/10 shadow-[0_0_30px_rgba(59,130,246,0.15)]';
        default:
            return 'border-slate-700 bg-slate-900/50';
        }
    }
    // LIGHT MODE STYLES
    else {
        switch (color) {
        case 'emerald':
            return isOffline ? 'border-red-200 bg-red-50' : 'border-emerald-200 bg-emerald-50 shadow-md shadow-emerald-500/5';
        case 'amber':
            return isOffline ? 'border-red-200 bg-red-50' : 'border-amber-200 bg-amber-50 shadow-md shadow-amber-500/5';
        case 'blue':
            return isOffline ? 'border-red-200 bg-red-50' : 'border-blue-200 bg-blue-50 shadow-md shadow-blue-500/5';
        default:
            return 'border-slate-200 bg-white';
        }
    }
  };

  const getTextClasses = (color) => {
    if (isOffline) return 'text-red-500';
    switch (color) {
      case 'emerald': return isDarkMode ? 'text-emerald-400' : 'text-emerald-600';
      case 'amber': return isDarkMode ? 'text-amber-400' : 'text-amber-600';
      case 'blue': return isDarkMode ? 'text-blue-400' : 'text-blue-600';
      default: return isDarkMode ? 'text-slate-200' : 'text-slate-700';
    }
  };

  const getStatusDot = (color) => {
    if (isOffline) return 'bg-red-500 animate-pulse';
    switch (color) {
      case 'emerald': return 'bg-emerald-500 animate-pulse';
      case 'amber': return 'bg-amber-500';
      case 'blue': return 'bg-blue-500';
      default: return 'bg-slate-500';
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className={`relative rounded-xl p-6 border transition-all duration-500 ${getColorClasses(zone.color)} h-full flex flex-col backdrop-blur-md`}
    >
      {/* Header */}
      <div className="mb-4">
        <div className="flex items-center gap-2 mb-2">
          <div className={`w-3 h-3 rounded-full ${getStatusDot(zone.color)}`} />
          <span className={`text-xs font-bold tracking-wider uppercase ${getTextClasses(zone.color)}`}>
            {isOffline ? 'OFFLINE' : zone.status === 'active' ? 'ACTIVE' : 'STANDBY'}
          </span>
        </div>
        <h3 className={`text-2xl font-bold mb-1 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{zone.name}</h3>
        <p className={`text-sm font-semibold tracking-wide uppercase ${getTextClasses(zone.color)} opacity-80`}>
          {zone.role}
        </p>
      </div>

      {/* Description */}
      <p className={`text-sm mb-6 leading-relaxed ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
        {isOffline ? "Connection lost. Workloads failed over." : zone.description}
      </p>

      {/* Features List */}
      <ul className="space-y-3 mt-auto">
        {zone.features.map((feature, idx) => (
          <li key={idx} className={`flex items-start gap-3 text-sm group ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
            <span className={`mt-0.5 ${getTextClasses(zone.color)} group-hover:scale-110 transition-transform`}>
              {getIcon(feature.text)}
            </span>
            <span className={isOffline ? 'opacity-70 line-through' : ''}>
              {feature.text}
            </span>
          </li>
        ))}
      </ul>

      {/* Background Decor (Only in Dark Mode for Glow) */}
      {!isOffline && isDarkMode && (
        <div className={`absolute -right-12 -top-12 w-48 h-48 rounded-full blur-[60px] opacity-10 pointer-events-none
          ${zone.color === 'emerald' ? 'bg-emerald-500' :
            zone.color === 'amber' ? 'bg-amber-500' : 'bg-blue-500'}`}
        />
      )}
    </motion.div>
  );
};

export default ZoneCard;
